import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@user/components/Header";
import Footer from "@user/components/Footer";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Download, FileText, ArrowLeft, ShieldCheck, Calendar, Clock, Video, MessageSquare, Phone } from "lucide-react";

const OUTCOME_LABELS = {
  advice_provided: "Advice Provided",
  matter_resolved: "Matter Resolved",
  follow_up_recommended: "Follow-up Consultation Recommended",
  representation_required: "Representation May Be Required",
  documentation_required: "Documentation Required",
  further_evidence_required: "Further Evidence Required",
};
const FOLLOW_UP_LABELS = { "7d": "7 days", "15d": "15 days", "30d": "30 days", custom: "a custom date" };
const TYPE_ICON = { video: Video, audio: Phone, chat: MessageSquare };

export default function ConsultationRecord() {
  const { id: cid } = useParams();
  const [state, setState] = useState({ loading: true, error: null, available: false });
  const [lcr, setLcr] = useState(null);
  const [consultation, setConsultation] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setState({ loading: true, error: null, available: false });
    try {
      const r = await api.get(`/consultations/${cid}/record`);
      setLcr(r.data.lcr || null);
      setConsultation(r.data.consultation || null);
      setState({ loading: false, error: null, available: !!r.data.available });
    } catch (e) {
      setState({ loading: false, error: e?.response?.data?.detail || "Could not load record", available: false });
    }
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/consultations/${cid}/record/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safe = (lcr?.matter_title || "consultation-record").trim().replace(/[^\w\-.]+/g, "-");
      a.href = url; a.download = `${safe}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not download");
    } finally { setDownloading(false); }
  };

  const downloadAttachment = async (a) => {
    try {
      const res = await api.get(`/consultations/${cid}/lcr/attachments/${a.id}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: a.content_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const el = document.createElement("a");
      el.href = url; el.download = a.name || "attachment";
      document.body.appendChild(el); el.click(); el.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download attachment");
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAFAFA]">
        <div className="h-10 w-10 rounded-full border-4 border-slate-200 border-t-[#0A2540] animate-spin" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <p className="text-rose-600 font-bold">{state.error}</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!state.available) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <div className="inline-flex h-16 w-16 rounded-2xl bg-[#FBF4DF] text-[#8a6d14] items-center justify-center mb-4">
            <FileText size={28}/>
          </div>
          <h1 className="text-2xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }} data-testid="lcr-not-ready">
            Your record isn&apos;t ready yet
          </h1>
          <p className="mt-2 text-slate-500">
            Your advocate is preparing the Legal Consultation Record for this consultation. You&apos;ll be notified as soon as it&apos;s published.
          </p>
          <Link to="/dashboard?tab=consults" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#0A2540] hover:underline">
            <ArrowLeft size={14}/> Back to dashboard
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const TypeIcon = TYPE_ICON[consultation?.consultation_type] || MessageSquare;
  const info = lcr.consultation_info || {};
  const fu = lcr.follow_up || {};

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="consultation-record-page">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/dashboard?tab=records" className="text-xs font-semibold text-slate-500 hover:text-[#0A2540] inline-flex items-center gap-1">
          <ArrowLeft size={12}/> My Consultation Records
        </Link>

        <div className="mt-4 bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
          <div className="p-6 sm:p-8 bg-gradient-to-br from-[#0A2540] to-[#173a63] text-white">
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#D4AF37]">Legal Consultation Record</p>
            <h1 className="mt-2 text-2xl sm:text-3xl font-extrabold" style={{ fontFamily: "Poppins" }}>
              {lcr.matter_title || "Consultation"}
            </h1>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs opacity-90">
              <span className="inline-flex items-center gap-1"><ShieldCheck size={12}/> {info.lawyer_name || "—"}</span>
              <span className="inline-flex items-center gap-1"><Calendar size={12}/> {info.date || "—"}</span>
              <span className="inline-flex items-center gap-1"><Clock size={12}/> {info.duration_minutes || 30} min</span>
              <span className="inline-flex items-center gap-1"><TypeIcon size={12}/> {(info.consultation_type || "chat").toUpperCase()}</span>
              <span className="text-[#D4AF37] font-bold">Version v{lcr.version || 1}</span>
            </div>
          </div>

          <div className="p-6 sm:p-8 space-y-6">
            <ClientSection title="Issue Discussed" body={lcr.client_issue} testid="client-issue"/>
            <BulletSection title="Facts Shared" items={lcr.facts} testid="facts"/>
            <BulletSection title="Documents Reviewed" items={(lcr.documents_reviewed || []).map(d => d.title)} testid="docs"/>
            <ClientSection title="Legal Opinion" body={lcr.legal_opinion} testid="legal-opinion"/>
            <BulletSection title="Recommended Next Steps" items={lcr.recommended_steps} testid="steps"/>
            {fu.recommended && (
              <div data-testid="followup-block">
                <h3 className="text-xs font-bold uppercase tracking-widest text-[#D4AF37]">Follow-up Consultation</h3>
                <p className="mt-1 text-sm text-slate-700">
                  Recommended after <b>{FOLLOW_UP_LABELS[fu.after] || fu.after || "—"}</b>
                  {fu.after === "custom" && fu.after_date ? ` (${fu.after_date})` : ""}
                  {fu.note ? ` — ${fu.note}` : ""}
                </p>
              </div>
            )}
            {(lcr.outcome || []).length > 0 && (
              <div data-testid="outcome-block">
                <h3 className="text-xs font-bold uppercase tracking-widest text-[#D4AF37]">Consultation Outcome</h3>
                <div className="mt-2 flex flex-wrap gap-2">
                  {lcr.outcome.map((o) => (
                    <span key={o} className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100">
                      {OUTCOME_LABELS[o] || o}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {(lcr.attachments || []).length > 0 && (
              <div data-testid="attachments-block">
                <h3 className="text-xs font-bold uppercase tracking-widest text-[#D4AF37]">Attachments</h3>
                <ul className="mt-2 space-y-1">
                  {lcr.attachments.map((a) => (
                    <li key={a.id} className="text-sm bg-slate-50 rounded-xl px-3 py-2 flex items-center gap-2">
                      <FileText size={14} className="text-slate-500 shrink-0"/>
                      <span className="flex-1 truncate">{a.name}</span>
                      {a.storage_key && (
                        <button
                          onClick={() => downloadAttachment(a)}
                          className="inline-flex items-center gap-1 text-[11px] font-bold text-[#0A2540] hover:underline shrink-0"
                          data-testid={`download-attachment-${a.id}`}
                        >
                          <Download size={12}/> Download
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="border-t border-slate-100 p-4 sm:p-6 flex flex-wrap items-center gap-3 bg-slate-50/50">
            <button
              onClick={downloadPdf}
              disabled={downloading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0A2540] text-[#D4AF37] font-bold text-sm disabled:opacity-60"
              data-testid="download-pdf-btn"
            >
              <Download size={14}/> {downloading ? "Downloading…" : "Download PDF"}
            </button>
            <p className="text-xs text-slate-500 flex-1">Internal reviewer notes are not included in the PDF.</p>
            <Link to="/dashboard?tab=records" className="text-xs font-semibold text-[#0A2540] hover:underline">All records →</Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function ClientSection({ title, body, testid }) {
  if (!body) return null;
  return (
    <div data-testid={`sec-${testid}`}>
      <h3 className="text-xs font-bold uppercase tracking-widest text-[#D4AF37]">{title}</h3>
      <p className="mt-1 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{body}</p>
    </div>
  );
}

function BulletSection({ title, items, testid }) {
  const list = (items || []).filter(Boolean);
  if (list.length === 0) return null;
  return (
    <div data-testid={`sec-${testid}`}>
      <h3 className="text-xs font-bold uppercase tracking-widest text-[#D4AF37]">{title}</h3>
      <ul className="mt-2 space-y-1">
        {list.map((it) => (
          <li key={it} className="text-sm text-slate-700 pl-4 relative">
            <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[#0A2540]" />
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}
